import { authRedirectInFlight, authRedirectLoopBreaker } from '@shared/auth-client/browserLoopBreaker';
import { AuthFlowError } from '@shared/auth-client/types';
import axios from 'axios';
import firebase from 'firebase';
import Cookies from 'js-cookie';
import { v4 } from 'uuid';

import { getClusterAwareBaseUrl } from '@/utils/clusterAwareUrl';

import router from '../router';
import { MUT_SET_AUTH_TOKENS, store } from '../store';

interface JwtPayload {
  iat: number;
  exp: number;
  aud: string[];
  iss: string;
  sub: string;
  jti: string;
}

interface AuthorizationRequest {
  response_type: string;
  client_id: string;
  state: string;
  redirect_uri: string;
  scope: string;
  code_challenge: string;
  code_challenge_method: string;
}

interface RequestRecord {
  request: AuthorizationRequest;
  requestedAt: string;
  codeVerifier: string;
  /** The request carried an escalation, so its org was chosen deliberately rather than remembered. */
  escalated?: boolean;
}

/**
 * Whether a request rejected with `invalid_scope` may be retried with no org.
 * Only a request that asked for an org can be, so the org-less retry itself is
 * terminal — that is what bounds this to a single retry. An escalation's org is
 * the whole point of the request, so dropping it would silently sign the user
 * in as themselves instead.
 */
function canRetryWithoutOrg(record: RequestRecord): boolean {
  return !record.escalated && record.request.scope.split(' ').some((s) => s.startsWith('orgId:'));
}

interface TokenResponse {
  access_token?: string;
  expires_in: number;
  scope: string;
  refresh_token?: string;
  id_token?: string;
  token_type: string;
}

export type IdTokenPayload = JwtPayload & { name: string; email: string; escalationOrgs: string[] };
export type AccessTokenPayload = JwtPayload & {
  userId: string;
  orgId: string | undefined;
  loadDynamically: boolean;
  loginMethod: 'saml' | 'firebase' | 'xero';
  scopes: string[];
  version: number;
};

export interface AuthTokens {
  accessToken: {
    token: string;
    orgId: string | undefined;
    expiry: Date;
  };
  idToken: {
    payload: IdTokenPayload;
    token: string;
    expiry: Date;
  };
}

export class AuthService {
  /** @deprecated This is part of the old auth service and is not compatible with BW auth */
  async logout() {
    try {
      const p1 = axios.get(`${process.env.VUE_APP_API_URL}logout`, {
        withCredentials: true,
      });
      const p2 = axios.get(`${getClusterAwareBaseUrl(process.env.VUE_APP_RPT_API_URL!)}logout`, {
        withCredentials: true,
      });
      const p3 = firebase.auth().signOut();
      await Promise.all([p1, p2, p3]);
    } catch (e) {
      console.log(e);
      return undefined;
    }
  }

  private b64UrlDecode(data: string): string {
    const b64 = data.replaceAll('-', '+').replaceAll('_', '/');
    const dataString = atob(b64);

    return dataString;
  }

  private b64UrlEncode(data: Uint8Array | ArrayBuffer | string): string {
    let stringData: string;
    if (typeof data === 'string') {
      stringData = data;
    } else {
      const uint8Data = data instanceof Uint8Array ? data : new Uint8Array(data);
      stringData = String.fromCharCode(...uint8Data);
    }
    const b64 = btoa(stringData);

    // Confirm to base64url standards, see RFC-4648
    return b64.replaceAll('+', '-').replaceAll('/', '_').replaceAll('=', '');
  }

  public parseJwt(token: string): JwtPayload {
    const data = this.b64UrlDecode(token.split('.')[1]);
    const jsonPayload = decodeURIComponent(
      data
        .split('')
        .map(function (c) {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join('')
    );

    return JSON.parse(jsonPayload);
  }

  /**
   * The terminal failure from the most recent sign-in attempt, if any.
   * main.ts drives handleAuthRedirect() and deliberately swallows the error,
   * but Auth.vue renders on the very same URL and must know the exchange
   * failed — otherwise it reads "no user" as "not signed in yet" and starts
   * another round trip to the IdP, which is the crash loop.
   */
  private static lastAuthFlowError: AuthFlowError | undefined;

  public static getLastAuthFlowError(): AuthFlowError | undefined {
    return AuthService.lastAuthFlowError;
  }

  public static clearLastAuthFlowError() {
    AuthService.lastAuthFlowError = undefined;
  }

  private fail(error: AuthFlowError): AuthFlowError {
    AuthService.lastAuthFlowError = error;
    return error;
  }

  /**
   * Forget the remembered org. The `orgId` cookie outlives the session that set
   * it, so the next sign-in in this browser — possibly a different account —
   * would otherwise ask the IdP for an org it cannot access (400 invalid_scope).
   */
  public static clearOrgHint() {
    Cookies.remove('orgId');
  }

  async sendToAuth(
    escalation?: { userId: string; orgId: string },
    opts?: {
      withoutOrg?: boolean;
      /** Authorize for THIS org rather than the remembered one. */
      orgId?: string;
      /**
       * Hint forwarded to the auth-svc login page after an
       * IDENTITY_PROVIDER_REQUIRED refusal: the org only admits Google
       * Workspace sign-ins, optionally from one domain. UX only.
       */
      stepUp?: { method: 'google_workspace'; hd?: string };
    }
  ) {
    if (authRedirectInFlight.active) {
      return;
    }

    // The IdP holds a session cookie, so /authorize answers instantly with a
    // fresh code. If the code exchange then fails deterministically, every
    // failure handler that calls back into sendToAuth() spins the browser
    // through authorize -> auth-redirect -> 400 -> authorize forever. Cap it.
    if (!authRedirectLoopBreaker.allow()) {
      throw this.fail(
        new AuthFlowError('too_many_redirects', {
          description: 'Stopped after repeated sign-in redirects that never completed.',
          terminal: true,
        })
      );
    }

    // Claim the redirect SYNCHRONOUSLY, before the first await below. This
    // method is async, so concurrent callers would otherwise all clear the
    // check above, then each record a redirect — tripping the breaker on what
    // is really a single hand-off.
    authRedirectInFlight.claim();

    try {
      await this.buildAndNavigateToAuthorize(escalation, opts?.withoutOrg, opts?.orgId, opts?.stepUp);
    } catch (e) {
      // Nothing was handed off, so release the claim and let a later attempt try.
      authRedirectInFlight.release();
      throw e;
    }
  }

  private async buildAndNavigateToAuthorize(
    escalation?: { userId: string; orgId: string },
    withoutOrg = false,
    targetOrgId?: string,
    stepUp?: { method: 'google_workspace'; hd?: string }
  ) {
    const url = new URL(process.env.VUE_APP_AUTH_SVC_URL ?? '');
    url.pathname = '/api/oauth/authorize';

    if (escalation) {
      url.searchParams.set('escalation', JSON.stringify(escalation));
    }
    if (stepUp) {
      // auth-svc shows why and steers Google's account chooser, and skips the
      // existing-session shortcut (that session is the one that was refused).
      url.searchParams.set('step_up', stepUp.method);
      if (stepUp.hd) url.searchParams.set('hd', stepUp.hd);
    }

    // See RFC-7636: 7.1. 32 bit sequence used as code verifier
    const codeVerifier = new Uint8Array(32);
    crypto.getRandomValues(codeVerifier);
    const hashed = await crypto.subtle.digest('SHA-256', codeVerifier);
    const codeChallenge = this.b64UrlEncode(hashed);
    const currentOrgId: string | undefined = withoutOrg
      ? undefined
      : targetOrgId ?? store.state.currentOrg?.id ?? Cookies.get('orgId');
    const authRequest = {
      response_type: 'code',
      client_id: 'web-app',
      // @ts-ignore - crypto.randomUUID() is not in this version of TS but it is available in the browser, we cannot update TS until we update Vue
      state: crypto.randomUUID(),
      redirect_uri: process.env.VUE_APP_BASE_URL + '/#/auth-redirect',
      scope: 'openid' + (currentOrgId ? ` orgId:${currentOrgId}` : ''),
      code_challenge: codeChallenge,
      code_challenge_method: 'S256',
    };

    // Save request and verifier in local storage for when we get redirected back
    localStorage.setItem(
      'authorizationRequest',
      JSON.stringify({
        request: authRequest,
        codeVerifier: this.b64UrlEncode(codeVerifier),
        requestedAt: new Date().toISOString(),
        escalated: !!escalation,
      })
    );

    for (const [key, val] of Object.entries(authRequest)) {
      url.searchParams.set(key, val);
    }

    authRedirectLoopBreaker.record();
    window.location.href = url.toString();
  }

  /**
   * Drop `code`/`state` from the address bar. The authorization code is
   * single-use and, by the time this is called, already burned — leaving it in
   * the URL means a reload (or the back button) re-POSTs it and reproduces the
   * same failure. `history.replaceState` is used rather than assigning
   * `location.hash` so this does not fire a hashchange and kick vue-router
   * into a navigation.
   *
   * Tab-local: touches only `window.location`, never shared storage.
   */
  private clearAuthRedirectParams() {
    try {
      const { href } = window.location;
      const hashIndex = href.indexOf('#');
      if (hashIndex === -1) return;
      const base = href.slice(0, hashIndex);
      const hash = href.slice(hashIndex);
      const queryIndex = hash.indexOf('?');
      if (queryIndex === -1) return;
      const params = new URLSearchParams(hash.slice(queryIndex + 1));
      params.delete('code');
      params.delete('state');
      const rest = params.toString();
      window.history.replaceState(null, '', base + hash.slice(0, queryIndex) + (rest ? `?${rest}` : ''));
    } catch (e) {
      console.warn('Failed to strip the auth redirect params from the URL', e);
    }
  }

  /**
   * Tear down a flow that cannot be completed, so nothing retries it.
   *
   * Only safe once the callback's `state` has been matched against the stored
   * request — `authorizationRequest` is a SINGLE localStorage key shared by
   * every tab on this origin, so removing it on a state mismatch would delete
   * whichever tab's login is currently in progress, not ours.
   */
  private abandonAuthorizationRequest() {
    localStorage.removeItem('authorizationRequest');
    this.clearAuthRedirectParams();
  }

  async handleAuthRedirect(): Promise<AuthTokens | undefined> {
    // Check for oauth redirect
    const requestRecordStr = localStorage.getItem('authorizationRequest');
    if (!requestRecordStr) {
      await this.sendToAuth();
      return;
    }
    const requestRecord: RequestRecord = JSON.parse(requestRecordStr);

    // Ensure request was issued not more than 30 minutes ago
    if (new Date(requestRecord.requestedAt).valueOf() < Date.now() - 30 * 60 * 1000) {
      await this.sendToAuth();
      return;
    }

    let url = new URL(window.location.href.replace('/#/', '/'));
    const code = url.searchParams.get('code');
    const state = url.searchParams.get('state');

    if (!code || !state) {
      await this.sendToAuth();
      return;
    }

    if (requestRecord.request.state !== state) {
      // A newer login started in another tab and overwrote the shared record, or
      // this callback was replayed. Either way the stored request is NOT ours:
      // deleting it would strand the tab that owns it (its own callback would
      // find nothing and start another full round trip). Only clean up this
      // tab's URL so the dead code cannot be replayed here.
      this.clearAuthRedirectParams();
      throw this.fail(
        new AuthFlowError('invalid_state', {
          description: 'There is another more recent login session started.',
          terminal: true,
        })
      );
    }

    url = new URL(process.env.VUE_APP_AUTH_SVC_URL ?? '');
    url.pathname = '/api/oauth/token';

    const resp = await axios.post<TokenResponse>(
      url.toString(),
      {
        grant_type: 'authorization_code',
        code,
        redirect_uri: requestRecord.request.redirect_uri,
        client_id: requestRecord.request.client_id,
        code_verifier: requestRecord.codeVerifier,
      },
      {
        withCredentials: true,
      }
    );

    // `axios.defaults.validateStatus` is `() => true` app-wide (see main.ts), so
    // a 400 RESOLVES here carrying the OAuth error body rather than throwing.
    // Check the status explicitly instead of inferring failure from missing
    // tokens. This is terminal: the code is single-use and the IdP has already
    // rejected it, so neither retrying it nor bouncing back to /authorize for a
    // fresh one can succeed — that bounce is precisely the crash loop.
    if (resp.status < 200 || resp.status >= 300) {
      const oauthError = (resp.data as unknown as { error?: string; error_description?: string }) ?? {};
      this.abandonAuthorizationRequest();
      if (oauthError.error === 'invalid_scope' && canRetryWithoutOrg(requestRecord)) {
        // This user cannot sign in to the requested org. Usually it came from
        // the cookie, left by another account that used this browser or by a
        // link for an org they cannot access. Forget it and sign in again
        // without one.
        AuthService.clearOrgHint();
        await this.sendToAuth(undefined, { withoutOrg: true });
        return;
      }
      throw this.fail(
        new AuthFlowError(oauthError.error || `token_http_${resp.status}`, {
          description: oauthError.error_description,
          terminal: true,
        })
      );
    }

    const idToken = resp.data.id_token;
    const accessToken = resp.data.access_token;
    const refreshToken = resp.data.refresh_token;

    if (!idToken || !accessToken || !refreshToken) {
      this.abandonAuthorizationRequest();
      throw this.fail(
        new AuthFlowError('incomplete_token_response', {
          description: 'Did not receive the expected tokens',
          terminal: true,
        })
      );
    }

    // Remove request from local storage
    localStorage.removeItem('authorizationRequest');
    // A completed login ends the loop-suspicion window.
    authRedirectLoopBreaker.reset();
    AuthService.clearLastAuthFlowError();

    const tokens = this.createTokensObj(accessToken, idToken);

    // Store refresh token in local storage
    this.storeRefreshToken(refreshToken);
    // Store tokens in store
    store.commit(MUT_SET_AUTH_TOKENS, tokens);
    // Initiate refresh timer
    this.initiateSessionKeepAlive(tokens);

    // Set current org if necessary
    await store.dispatch('setCurrentOrg', tokens.accessToken.orgId);

    return tokens;
  }

  public getJwtExpiry(token: string): Date {
    const payload = this.parseJwt(token);
    return new Date(payload.exp * 1e3);
  }

  async restoreTokens(): Promise<AuthTokens | undefined> {
    const refreshToken = localStorage.getItem('refreshToken');

    if (!refreshToken) {
      return Promise.resolve(undefined);
    }

    const refreshTokenExpiry = this.getJwtExpiry(refreshToken);

    if (refreshTokenExpiry < new Date()) {
      localStorage.removeItem('refreshToken');
      return Promise.resolve(undefined);
    }

    return this.refreshSession();
  }

  private createTokensObj(accessToken: string, idToken: string): AuthTokens {
    const idTokenPayload = this.parseJwt(idToken) as IdTokenPayload;
    const accessTokenPayload = this.parseJwt(accessToken) as AccessTokenPayload;

    return {
      accessToken: {
        token: accessToken,
        orgId: accessTokenPayload.orgId,
        expiry: new Date(accessTokenPayload.exp * 1000),
      },
      idToken: {
        payload: idTokenPayload,
        token: idToken,
        expiry: new Date(idTokenPayload.exp * 1000),
      },
    };
  }

  private static sessionKeepAliveTimeout: number | undefined;
  private static crossWindowCommsEstablished = false;
  public initiateSessionKeepAlive(currentAuthTokens: AuthTokens) {
    if (AuthService.sessionKeepAliveTimeout !== undefined) {
      window.clearTimeout(AuthService.sessionKeepAliveTimeout);
      AuthService.sessionKeepAliveTimeout = undefined;
    }
    let refreshTokenInMS = currentAuthTokens.accessToken.expiry.valueOf() - Date.now() - 30000;
    if (refreshTokenInMS < 0) {
      refreshTokenInMS = 0;
    }
    AuthService.sessionKeepAliveTimeout = window.setTimeout(async () => {
      AuthService.sessionKeepAliveTimeout = undefined;

      // Check session is still active
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) {
        return;
      }
      // 30 seconds before expiry, refresh the access token
      await this.refreshSession();
    }, refreshTokenInMS);
  }

  private async refreshSession(orgId?: string): Promise<AuthTokens | undefined> {
    const currentRefreshToken = localStorage.getItem('refreshToken');
    if (!currentRefreshToken || this.getJwtExpiry(currentRefreshToken) < new Date()) {
      console.log('Not refreshing session. ' + currentRefreshToken ? 'Refresh token expired' : 'No refresh token');
      localStorage.removeItem('refreshToken');
      return;
    }
    // An org passed in, or the one the store has selected, is a real choice. An
    // org read from the cookie is only a hint and may be stale.
    let orgIdFromCookie = false;
    if (!orgId) {
      orgId = store.state.currentOrg?.id;
    }
    if (!orgId) {
      orgId = Cookies.get('orgId');
      orgIdFromCookie = !!orgId;
    }

    try {
      const url = new URL(process.env.VUE_APP_AUTH_SVC_URL ?? '');
      url.pathname = '/api/oauth/token';

      const requestTokens = (scopeOrgId?: string) =>
        axios.post<TokenResponse>(
          url.toString(),
          {
            grant_type: 'refresh_token',
            refresh_token: currentRefreshToken,
            scope: 'openid' + (scopeOrgId ? ` orgId:${scopeOrgId}` : ''),
          },
          {
            withCredentials: true,
            // Resolve on 4xx so the OAuth error code below can be read.
            validateStatus: () => true,
          }
        );

      let resp = await requestTokens(orgId);

      // invalid_scope refuses only the org; the refresh token is still good.
      // Drop a cookie-borne org and ask once more without one.
      const oauthError = (resp.data as unknown as { error?: string } | undefined)?.error;
      if (orgIdFromCookie && resp.status >= 400 && resp.status < 500 && oauthError === 'invalid_scope') {
        AuthService.clearOrgHint();
        resp = await requestTokens();
      }

      const idToken = resp.data.id_token;
      const accessToken = resp.data.access_token;
      const refreshToken = resp.data.refresh_token;

      if (!idToken || !accessToken || !refreshToken) {
        throw new Error('Did not receive the expected tokens');
      }

      const tokens = this.createTokensObj(accessToken, idToken);

      // Store refresh token in local storage
      this.storeRefreshToken(refreshToken);
      // Store tokens in store
      store.commit(MUT_SET_AUTH_TOKENS, tokens);
      // A working session ends the loop-suspicion window.
      authRedirectLoopBreaker.reset();
      this.initiateSessionKeepAlive(tokens);

      // Set current org if necessary
      if (tokens.accessToken.orgId && store.state.currentOrg?.id !== tokens.accessToken.orgId) {
        await store.dispatch('setCurrentOrg', tokens.accessToken.orgId);
      }

      return tokens;
    } catch (e) {
      console.error('Failed to refresh session. Singing out.');
      console.error(e);
      router.push('/signout');
      throw e;
    }
  }

  /** Stores the refresh token in local storage if it is newer than the existing one */
  private storeRefreshToken(refreshToken: string) {
    const existingRefreshToken = localStorage.getItem('refreshToken');
    const existingRefreshTokenExpiry = existingRefreshToken ? this.getJwtExpiry(existingRefreshToken) : undefined;
    if (!existingRefreshTokenExpiry || this.getJwtExpiry(refreshToken) > existingRefreshTokenExpiry) {
      localStorage.setItem('refreshToken', refreshToken);
    }
  }

  public initCrossWindowComms() {
    if (AuthService.crossWindowCommsEstablished) {
      return;
    }
    AuthService.crossWindowCommsEstablished = true;
    window.addEventListener('storage', (e) => {
      if (e.key === 'refreshToken') {
        if (!e.newValue) {
          // Session was cleared in another window, so we need to sign out
          router.push('/signout');
        }
      } else if (e.key === null && e.newValue === null) {
        // The entire localStorage was cleared, so we need to sign out
        router.push('/signout');
      }
    });
  }

  public async switchAccessOrg(newOrgId: string) {
    await this.refreshSession(newOrgId);
  }
}
